// Client-side generator for the SOC 2 evidence package: renders the control
// register + evidence records as .docx files in the exact layout AND STYLING
// of the Mindshine SOC 2 Audit Evidence Package (Igor's template pack), and
// bundles them into one zip — entirely in the browser, no server rendering.
//
// Style parity is deliberate and extracted from the original documents:
//   Normal = Aptos 9pt · title = centered bold 18pt · subtitle = centered
//   italic · Heading1 = bold #365F91 14pt · Heading2 = bold #4F81BD 13pt ·
//   meta table = centered, borderless, label cells shaded #D9EAF7 ·
//   data tables = TableGrid borders, centered, header row shaded #B4C6E7 ·
//   page header = right-aligned "Mindshine Technologies Inc. | Confidential
//   - Audit Evidence" · footer = centered provenance notice.
//
// A .docx is itself a zip of XML parts, so one dependency-free STORE-method
// zip writer serves both the inner documents and the outer package.
//
// Provenance is carried INTO the documents, never laundered out: sample
// records get a bold SAMPLE/DEMO notice and always read as drafts; compiled
// records list their compiled_from source pointers; the meta table prints
// DRAFT vs FINAL with signer and date.

const COMPANY = "Mindshine Technologies Inc.";
const PLATFORMS = "TIVA Cortex / TIVA Flow";
const AUDIT_TYPE = "SOC 2 Type II";
const PKG_STATUS = {
  sample: "Sample / Demo Data — not evidence of an operated control",
  logged: "Management-prepared evidence (logged at occurrence)",
  compiled: "Management-prepared evidence (compiled from system records)",
  ceremony: "Management-prepared evidence (performed & recorded)",
};
const META_FILL = "D9EAF7";   // meta-table label cells (from the originals)
const HEAD_FILL = "B4C6E7";   // data-table header row (from the originals)

// ---------------------------------------------------------------------------
// Minimal zip writer (STORE method — valid for .docx and for the bundle).
// ---------------------------------------------------------------------------
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function dosDateTime(d = new Date()) {
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
  const date = (((d.getFullYear() - 1980) & 0x7f) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  return { time, date };
}

// entries: [{ name: string, data: Uint8Array }] -> Uint8Array (zip bytes)
export function makeZip(entries) {
  const enc = new TextEncoder();
  const { time, date } = dosDateTime();
  const locals = [];
  const centrals = [];
  let offset = 0;
  for (const e of entries) {
    const name = enc.encode(e.name);
    const crc = crc32(e.data);
    const head = new DataView(new ArrayBuffer(30));
    head.setUint32(0, 0x04034b50, true);
    head.setUint16(4, 20, true);            // version needed
    head.setUint16(6, 0x0800, true);        // UTF-8 names
    head.setUint16(8, 0, true);             // STORE
    head.setUint16(10, time, true);
    head.setUint16(12, date, true);
    head.setUint32(14, crc, true);
    head.setUint32(18, e.data.length, true);
    head.setUint32(22, e.data.length, true);
    head.setUint16(26, name.length, true);
    head.setUint16(28, 0, true);
    locals.push(new Uint8Array(head.buffer), name, e.data);

    const cen = new DataView(new ArrayBuffer(46));
    cen.setUint32(0, 0x02014b50, true);
    cen.setUint16(4, 20, true);
    cen.setUint16(6, 20, true);
    cen.setUint16(8, 0x0800, true);
    cen.setUint16(10, 0, true);
    cen.setUint16(12, time, true);
    cen.setUint16(14, date, true);
    cen.setUint32(16, crc, true);
    cen.setUint32(20, e.data.length, true);
    cen.setUint32(24, e.data.length, true);
    cen.setUint16(28, name.length, true);
    cen.setUint32(42, offset, true);
    centrals.push(new Uint8Array(cen.buffer), name);
    offset += 30 + name.length + e.data.length;
  }
  const cdSize = centrals.reduce((n, b) => n + b.length, 0);
  const eocd = new DataView(new ArrayBuffer(22));
  eocd.setUint32(0, 0x06054b50, true);
  eocd.setUint16(8, entries.length, true);
  eocd.setUint16(10, entries.length, true);
  eocd.setUint32(12, cdSize, true);
  eocd.setUint32(16, offset, true);
  const parts = [...locals, ...centrals, new Uint8Array(eocd.buffer)];
  const out = new Uint8Array(parts.reduce((n, b) => n + b.length, 0));
  let pos = 0;
  for (const p of parts) { out.set(p, pos); pos += p.length; }
  return out;
}

// ---------------------------------------------------------------------------
// Minimal OOXML (WordprocessingML) document builder — original styling.
// ---------------------------------------------------------------------------
const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const para = (text, { style, bold, italic, size, center } = {}) => {
  const pPr = (style || center) ? `<w:pPr>${style ? `<w:pStyle w:val="${style}"/>` : ""}${center ? `<w:jc w:val="center"/>` : ""}</w:pPr>` : "";
  const rPr = (bold || italic || size) ? `<w:rPr>${bold ? "<w:b/>" : ""}${italic ? "<w:i/>" : ""}${size ? `<w:sz w:val="${size}"/>` : ""}</w:rPr>` : "";
  return `<w:p>${pPr}<w:r>${rPr}<w:t xml:space="preserve">${esc(text)}</w:t></w:r></w:p>`;
};

const cell = ({ text, bold, fill, width }) => {
  const tcPr = `<w:tcPr>${width ? `<w:tcW w:w="${width}" w:type="dxa"/>` : `<w:tcW w:w="0" w:type="auto"/>`}${fill ? `<w:shd w:fill="${fill}"/>` : ""}</w:tcPr>`;
  return `<w:tc>${tcPr}<w:p><w:r>${bold ? "<w:rPr><w:b/></w:rPr>" : ""}<w:t xml:space="preserve">${esc(text)}</w:t></w:r></w:p></w:tc>`;
};

const TBL_LOOK = `<w:tblLook w:firstColumn="1" w:firstRow="1" w:lastColumn="0" w:lastRow="0" w:noHBand="0" w:noVBand="1" w:val="04A0"/>`;

// Borderless, centered meta table: rows of [label, value, label, value] with
// the label cells shaded — exactly like the originals' header block.
const metaTable = (pairs) => {
  const rows = [];
  for (let i = 0; i < pairs.length; i += 2) rows.push([pairs[i], pairs[i + 1]].filter(Boolean));
  const trs = rows.map((r) => `<w:tr>${r.map(([k, v]) =>
    cell({ text: k, fill: META_FILL, width: 2556 }) + cell({ text: v, width: 2556 })).join("")}</w:tr>`).join("");
  return `<w:tbl><w:tblPr><w:tblW w:w="0" w:type="auto"/><w:jc w:val="center"/>${TBL_LOOK}</w:tblPr>${trs}</w:tbl>`;
};

// Bordered (TableGrid) centered data table with the shaded header row.
const dataTable = (columns, rows) => {
  const head = `<w:tr>${columns.map((c) => cell({ text: c, fill: HEAD_FILL })).join("")}</w:tr>`;
  const body = rows.map((r) => `<w:tr>${columns.map((_, i) => cell({ text: r[i] ?? "" })).join("")}</w:tr>`).join("");
  return `<w:tbl><w:tblPr><w:tblStyle w:val="TableGrid"/><w:tblW w:w="0" w:type="auto"/><w:jc w:val="center"/>${TBL_LOOK}</w:tblPr>${head}${body}</w:tbl>`;
};

// Styles extracted from the original package (Aptos 9pt Normal; blue headings;
// TableGrid single borders; Header/Footer paragraph styles).
const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:qFormat/><w:rPr><w:rFonts w:ascii="Aptos" w:hAnsi="Aptos"/><w:sz w:val="18"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Heading1"><w:name w:val="heading 1"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:keepLines/><w:spacing w:before="480" w:after="0"/><w:outlineLvl w:val="0"/></w:pPr><w:rPr><w:b/><w:color w:val="365F91"/><w:sz w:val="28"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Heading2"><w:name w:val="heading 2"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:keepLines/><w:spacing w:before="200" w:after="0"/><w:outlineLvl w:val="1"/></w:pPr><w:rPr><w:b/><w:color w:val="4F81BD"/><w:sz w:val="26"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Header"><w:name w:val="header"/><w:basedOn w:val="Normal"/><w:pPr><w:spacing w:after="0" w:line="240" w:lineRule="auto"/></w:pPr></w:style>
<w:style w:type="paragraph" w:styleId="Footer"><w:name w:val="footer"/><w:basedOn w:val="Normal"/><w:pPr><w:spacing w:after="0" w:line="240" w:lineRule="auto"/></w:pPr></w:style>
<w:style w:type="table" w:styleId="TableGrid"><w:name w:val="Table Grid"/><w:pPr><w:spacing w:after="0" w:line="240" w:lineRule="auto"/></w:pPr><w:tblPr><w:tblInd w:w="0" w:type="dxa"/><w:tblBorders><w:top w:val="single" w:sz="4" w:space="0" w:color="auto"/><w:left w:val="single" w:sz="4" w:space="0" w:color="auto"/><w:bottom w:val="single" w:sz="4" w:space="0" w:color="auto"/><w:right w:val="single" w:sz="4" w:space="0" w:color="auto"/><w:insideH w:val="single" w:sz="4" w:space="0" w:color="auto"/><w:insideV w:val="single" w:sz="4" w:space="0" w:color="auto"/></w:tblBorders><w:tblCellMar><w:top w:w="40" w:type="dxa"/><w:left w:w="80" w:type="dxa"/><w:bottom w:w="40" w:type="dxa"/><w:right w:w="80" w:type="dxa"/></w:tblCellMar></w:tblPr></w:style>
</w:styles>`;

const W_NS = `xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"`;
const R_NS = `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"`;

// body XML + footer text -> .docx bytes (with the original page header, the
// given footer notice, and the original page geometry).
export function makeDocx(bodyXml, footerText) {
  const enc = new TextEncoder();
  const headerXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:hdr ${W_NS}><w:p><w:pPr><w:pStyle w:val="Header"/><w:jc w:val="right"/></w:pPr><w:r><w:t xml:space="preserve">${esc(COMPANY)} | Confidential - Audit Evidence</w:t></w:r></w:p></w:hdr>`;
  const footerXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:ftr ${W_NS}><w:p><w:pPr><w:pStyle w:val="Footer"/><w:jc w:val="center"/></w:pPr><w:r><w:t xml:space="preserve">${esc(footerText || "Generated from the cortex audit store")}</w:t></w:r></w:p></w:ftr>`;
  const sectPr = `<w:sectPr><w:headerReference w:type="default" r:id="rIdH"/><w:footerReference w:type="default" r:id="rIdF"/><w:pgSz w:w="12240" w:h="15840"/><w:pgMar w:top="936" w:right="1008" w:bottom="936" w:left="1008" w:header="720" w:footer="720" w:gutter="0"/></w:sectPr>`;
  const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document ${W_NS} ${R_NS}><w:body>${bodyXml}${sectPr}</w:body></w:document>`;
  return makeZip([
    { name: "[Content_Types].xml", data: enc.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/><Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/><Override PartName="/word/header1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/><Override PartName="/word/footer1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/></Types>`) },
    { name: "_rels/.rels", data: enc.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>`) },
    { name: "word/_rels/document.xml.rels", data: enc.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/><Relationship Id="rIdH" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="header1.xml"/><Relationship Id="rIdF" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer" Target="footer1.xml"/></Relationships>`) },
    { name: "word/styles.xml", data: enc.encode(STYLES_XML) },
    { name: "word/header1.xml", data: enc.encode(headerXml) },
    { name: "word/footer1.xml", data: enc.encode(footerXml) },
    { name: "word/document.xml", data: enc.encode(document) },
  ]);
}

// ---------------------------------------------------------------------------
// Evidence-package document builders (layout + styling of the originals).
// ---------------------------------------------------------------------------
const jsonish = (v, fb) => { if (v == null) return fb; if (typeof v === "string") { try { return JSON.parse(v); } catch { return fb; } } return v; };
const day = (v) => String(v || "").slice(0, 10);
const slugify = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

const titleBlock = (text, sub) => para(text, { center: true, bold: true, size: 36 }) + (sub ? para(sub, { center: true, italic: true }) : "") + para("");

const footerFor = (anySample) => anySample
  ? "SAMPLE / DEMO DATA - synthetic test records; not evidence of an operated control"
  : "Management-prepared evidence - generated from the cortex audit store; validate before external distribution";

function recordBody(r) {
  const src = r.source || "";
  const signed = r.status === "final";
  const form = jsonish(r.form, {}) || {};
  const fields = form.fields || {};
  let xml = titleBlock(r.title, src === "sample" ? "Sample Evidence Record" : "Evidence Record");
  xml += metaTable([
    ["Company", COMPANY],
    ["Package Status", PKG_STATUS[src] || src],
    ["Platforms", PLATFORMS],
    ["Audit Type", AUDIT_TYPE],
    ["Period", r.period_start ? `${day(r.period_start)} — ${day(r.period_end)}` : day(r.created_at)],
    ["Prepared By", r.created_by || ""],
    ["Recorded", String(r.created_at || "").slice(0, 19).replace("T", " ")],
    ["Record Status", signed ? `FINAL — signed by ${r.signed_by} on ${day(r.signed_at)}` : "DRAFT — not signed"],
  ]);
  if (src === "sample") {
    xml += para("SAMPLE / DEMO DATA: this record is synthetic test data. It cannot be signed and must never be presented as evidence of an operated control.", { bold: true, center: true });
  }
  const ordered = fields["Control Mapping"] ? [["Control Mapping", fields["Control Mapping"]]] : [];
  for (const [k, v] of Object.entries(fields)) if (k !== "Control Mapping") ordered.push([k, v]);
  for (const [k, v] of ordered) xml += para(k, { style: "Heading1" }) + para(String(v));
  for (const s of form.sections || []) {
    xml += para(s.title || "Details", { style: "Heading2" }) + dataTable(s.columns || [], s.rows || []);
  }
  const cf = jsonish(r.compiled_from, []) || [];
  if (cf.length) {
    xml += para("Source Records (provenance)", { style: "Heading2" });
    xml += dataTable(["System", "Detail"], cf.map((c) => [c.system || "", Object.entries(c).filter(([k]) => k !== "system").map(([k, v]) => `${k}=${v}`).join("; ")]));
  }
  xml += para("Sign-off", { style: "Heading2" });
  xml += dataTable(["Reviewer", "Title", "Date", "Signature / Approval Ref"],
    signed ? [[r.signed_by || "", "", day(r.signed_at), "signed via cortex audit portal"]] : [["— (draft, unsigned)", "", "", ""]]);
  return xml;
}

function registerBody(controls, projectName) {
  let xml = titleBlock("Master SOC 2 Control & Evidence Register", `Generated from the cortex audit store — project ${projectName}`);
  xml += metaTable([
    ["Company", COMPANY],
    ["Package Status", "Generated register (live control state)"],
    ["Platforms", PLATFORMS],
    ["Audit Type", AUDIT_TYPE],
    ["Generated", day(new Date().toISOString())],
  ]);
  xml += para("Register", { style: "Heading1" });
  xml += dataTable(["ID", "TSC", "Control", "Owner", "Frequency", "Required Evidence", "Applies To", "Status", "Evidence Records"],
    controls.map((c) => [c.control_id, c.tsc || "", c.control || "", c.owner || "", c.frequency || "", c.required_evidence || "", c.applies_to || "", c.status || "",
      c.evidence_count ? `${c.evidence_count} (last ${day(c.last_evidence_at)})` : "0"]));
  return xml;
}

function readmeBody(projectName, files, anySample) {
  let xml = titleBlock(`SOC 2 Evidence Package — ${projectName}`, "Generated from the cortex audit store");
  const pairs = [
    ["Company", COMPANY],
    ["Platforms", PLATFORMS],
    ["Generated", day(new Date().toISOString())],
    ["Contents", `${files.length} documents`],
  ];
  if (anySample) pairs.push(["Notice", "This package contains SAMPLE / DEMO records (marked in each document). Sample records are synthetic test data and are not evidence of operated controls."], ["", ""]);
  xml += metaTable(pairs.filter((p) => p[0] !== ""));
  xml += para("Index", { style: "Heading1" });
  for (const f of files) xml += para(f);
  return xml;
}

// Build the whole package. `records` must be FULL rows (evidence_get shape).
export function buildEvidencePackage({ projectName, controls, records }) {
  const sorted = [...(records || [])].sort((a, b) => String(a.control_id).localeCompare(String(b.control_id)) || a.id - b.id);
  const anySample = sorted.some((r) => r.source === "sample");
  const footer = footerFor(anySample);
  const entries = [];
  const names = [];
  if (controls?.length) {
    entries.push({ name: "01_Master_Control_Evidence_Register.docx", data: makeDocx(registerBody(controls, projectName), footer) });
    names.push("01_Master_Control_Evidence_Register.docx");
  }
  sorted.forEach((r, i) => {
    const name = `${String(i + 2).padStart(2, "0")}_${r.control_id}_${slugify(r.title)}.docx`;
    entries.push({ name, data: makeDocx(recordBody(r), r.source === "sample" ? footerFor(true) : footerFor(false)) });
    names.push(name);
  });
  entries.unshift({ name: "00_README_and_Index.docx", data: makeDocx(readmeBody(projectName, ["00_README_and_Index.docx", ...names], anySample), footer) });
  const zipName = `Mindshine_SOC2_Evidence_${projectName}_${day(new Date().toISOString())}.zip`;
  return { name: zipName, bytes: makeZip(entries) };
}

export function downloadBytes(name, bytes, mime = "application/zip") {
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
